import { BAOCustomElement } from '@bao/custom-element'
import { sectionRenderer } from '@bao/section-rendering'
import { getElement } from '@bao/utils'

let dropdownLoaded = false

class HeaderDropdown extends BAOCustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.dragging = false

    if (dropdownLoaded) return

    sectionRenderer
      .fetch(
        this.dataset.sectionId,
        `${window.Shopify.routes.root}?view=dropdown`,
      )
      .then((res) => {
        dropdownLoaded = true

        const newDOM = sectionRenderer.convertToDOM(
          res[this.dataset.sectionId],
        )
        const myself = newDOM.querySelector(this.localName)

        this.outerHTML = myself.outerHTML
        this.alwaysOpen()
      })
      .catch(() => {})
  }

  setupListeners () {
    super.setupListeners()

    this.addEventListener('click', this.handleToggleButtonClick.bind(this))

    const triggerEls = this.querySelectorAll(this.elements.trigger)

    triggerEls.forEach((triggerEl) => {
      triggerEl.addEventListener(
        'pointerenter',
        this.onPointerEnter.bind(this),
      )
    })

    this.addEventListener('pointerleave', this.onPointerLeave.bind(this))

    this.addEventListener('focusin', this.handleFocusIn.bind(this))
    this.addEventListener('keyup', this.handleEscapePress.bind(this))

    this.addEventListener('pointerdown', () => {
      this.dragging = true
    })

    this.addEventListener('pointerup', () => {
      this.dragging = false
    })
  }

  /**
   * @param {ClickEvent} event
   *
   * @returns {void}
   */
  handleToggleButtonClick (event) {
    const menuToggleEl = event.target.closest(this.elements.toggle)

    if (!menuToggleEl) return

    const listItemEl = menuToggleEl.closest(this.elements.listItem)

    if (!listItemEl) return

    const triggerEl = getElement(this.elements.trigger, {
      context: listItemEl,
    })

    if (triggerEl.getAttribute('aria-expanded') === 'false') {
      this.openDropdown(triggerEl.dataset.id)
    } else {
      this.closeDropdowns()
    }
  }

  onPointerEnter (event) {
    this.dragging = false
    this.openDropdown(event.target.dataset.id)
  }

  onPointerLeave () {
    /* Prevent closing dropdown when dragging (i.e. dragging a carousel off screen) */
    if (this.dragging) return
    this.closeDropdowns()
  }

  /**
   * @param {FocusInEvent} event
   *
   * @returns {void}
   */
  handleFocusIn (event) {
    if (
      event.target.closest(this.localName) &&
      !event.target.closest('[aria-hidden="false"]')
    ) {
      /* This ensures an expanded dropdown closes when the user tabs out of it but focus remains within the header-dropdown element */
      this.closeDropdowns()
    }

    if (!event.target.closest(this.localName)) {
      /* This ensures all dropdowns are closed when focus leaves the header-dropdown element */
      this.closeDropdowns()
    }
  }

  /**
   * @param {KeyboardEvent} event
   *
   * @returns {void}
   */
  handleEscapePress (event) {
    if (event.code?.toUpperCase() !== 'ESCAPE') return
    this.closeDropdowns()
  }

  openDropdown (id) {
    const matchedDropdown = this.querySelector(
      `${this.elements.dropdown}#${id}`,
    )

    if (matchedDropdown) {
      const toggleEls = this.querySelectorAll(this.elements.toggle)
      const triggerEls = this.querySelectorAll(this.elements.trigger)
      const dropdownEls = this.querySelectorAll(this.elements.dropdown)

      toggleEls.forEach((menuTriggerEl) => {
        menuTriggerEl.setAttribute(
          'aria-expanded',
          menuTriggerEl.dataset.id === matchedDropdown.id,
        )
      })

      triggerEls.forEach((menuToggleEl) => {
        menuToggleEl.setAttribute(
          'aria-expanded',
          menuToggleEl.dataset.id === matchedDropdown.id,
        )
      })

      dropdownEls.forEach((menuDropdownEl) => {
        menuDropdownEl.setAttribute(
          'aria-hidden',
          menuDropdownEl !== matchedDropdown,
        )
      })

      document.dispatchEvent(
        new CustomEvent('header-dropdown-update', { detail: { id } }),
      )
    } else {
      this.closeDropdowns()
    }
  }

  /**
   * @returns {void}
   */
  closeDropdowns () {
    const menuDropdownToggleEls = this.querySelectorAll(this.elements.toggle)
    const menuTriggerEls = this.querySelectorAll(this.elements.trigger)
    const menuDropdownEls = this.querySelectorAll(this.elements.dropdown)

    if (menuDropdownToggleEls) {
      menuDropdownToggleEls.forEach((menuToggleEl) => {
        menuToggleEl.setAttribute('aria-expanded', false)
      })
    }

    if (menuTriggerEls) {
      menuTriggerEls.forEach((menuToggleEl) => {
        menuToggleEl.setAttribute('aria-expanded', false)
      })
    }

    if (menuDropdownEls) {
      menuDropdownEls.forEach((menuDropdownEl) => {
        menuDropdownEl.setAttribute('aria-hidden', true)
      })
    }

    document.dispatchEvent(new CustomEvent('header-dropdown-update'))
  }

  get elements () {
    return {
      dropdown: '[data-header-dropdown-el="menu-dropdown"]',
      listItem: '[data-header-dropdown-el="item"]',
      trigger: '[data-header-dropdown-el="menu-dropdown-trigger"]',
      toggle: '[data-header-dropdown-el="menu-dropdown-toggle"]',
    }
  }
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown)
}
